import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, FeetInches, RadioGroup, ResultTable } from '../_fitnessHelpers';

export default function IdealWeightCalc() {
  const [tab, setTab] = useState(0);
  const [age, setAge] = useState(25);
  const [gender, setGender] = useState('male');
  const [ft, setFt] = useState(5);
  const [inches, setInches] = useState(10);
  const [cm, setCm] = useState(178);
  const clear = () => { setAge(25); setGender('male'); setFt(5); setInches(10); setCm(178); };

  const c = useMemo(() => {
    const heightCm = tab === 0 ? ((+ft || 0) * 12 + (+inches || 0)) * 2.54 : +cm || 0;
    const heightIn = heightCm / 2.54;
    const inchesOver5ft = Math.max(0, heightIn - 60);
    const male = gender === 'male';
    const robinsonKg = (male ? 52 : 49) + (male ? 1.9 : 1.7) * inchesOver5ft;
    const millerKg = (male ? 56.2 : 53.1) + (male ? 1.41 : 1.36) * inchesOver5ft;
    const devineKg = (male ? 50 : 45.5) + 2.3 * inchesOver5ft;
    const hamwiKg = (male ? 48 : 45.5) + (male ? 2.7 : 2.2) * inchesOver5ft;
    const healthyLowKg = 18.5 * Math.pow(heightCm/100, 2);
    const healthyHighKg = 24.9 * Math.pow(heightCm/100, 2);
    const disp = kg => tab === 0 ? (kg / 0.453592).toFixed(1) + ' lbs' : kg.toFixed(1) + ' kg';
    return { robinsonKg, millerKg, devineKg, hamwiKg, healthyLowKg, healthyHighKg, disp };
  }, [tab, gender, ft, inches, cm]);

  return (
    <CalcShell look="cnet" title="Ideal Weight Calculator" subtitle="Compute ideal body weight (IBW) using popular formulas.">
      <CalcNetPanel
        slug="iw"
        activeTab={tab}
        onTabChange={setTab}
        onClear={clear}
        right={
          <div>
            <div className="text-sm text-slate-700 mb-2">The ideal weight based on popular formulas:</div>
            <ResultTable
              testid="iw-table"
              headers={['Formula', 'Ideal Weight']}
              rows={[
                ['Robinson (1983)', <span className="font-bold text-[#2f9c3f]">{c.disp(c.robinsonKg)}</span>],
                ['Miller (1983)', <span className="font-bold text-[#2f9c3f]">{c.disp(c.millerKg)}</span>],
                ['Devine (1974)', <span className="font-bold text-[#2f9c3f]">{c.disp(c.devineKg)}</span>],
                ['Hamwi (1964)', <span className="font-bold text-[#2f9c3f]">{c.disp(c.hamwiKg)}</span>],
                ['Healthy BMI Range', <span className="font-bold text-[#2f9c3f]">{c.disp(c.healthyLowKg)} - {c.disp(c.healthyHighKg)}</span>],
              ]}
            />
          </div>
        }
      >
        <Row label="Age"><UnitInput value={age} onChange={setAge} testid="iw-age" /><span className="text-slate-500 text-xs whitespace-nowrap">ages 2 – 80</span></Row>
        <Row label="Gender"><RadioGroup name="iw-g" value={gender} onChange={setGender} options={[{value:'male',label:'male'},{value:'female',label:'female'}]} /></Row>
        <Row label="Height">{tab === 0
          ? <div className="grid grid-cols-2 gap-2 flex-1"><FeetInches ft={ft} inches={inches} onFt={setFt} onIn={setInches} testidPrefix="iw-h" /></div>
          : <UnitInput value={cm} onChange={setCm} unit="cm" testid="iw-h-cm" />}</Row>
      </CalcNetPanel>

      <StepsPanel steps={[
        `Height above 5 ft (60 in) = ${Math.max(0, ((tab===0?(+ft||0)*12+(+inches||0):(+cm||0)/2.54) - 60)).toFixed(1)} in`,
        `Robinson (${gender}): base + ${(gender==='male'?1.9:1.7)} × inches over 5 ft = <b>${c.disp(c.robinsonKg)}</b>`,
        `Healthy BMI range (18.5–24.9): ${c.disp(c.healthyLowKg)} – ${c.disp(c.healthyHighKg)}`,
      ]} />
    </CalcShell>
  );
}
